/* eslint-disable max-lines-per-function */
import React from "react"
import { Box, Heading, Text } from "@chakra-ui/react"
import { StaticPageHeader } from "../components/commons/static-page-header"
import bg from "../assets/images/compliance.png"
import { breadCrumbFirstLevel, breadCrumbReportsAndResourcesSecondLevel, pageContentWidth } from "../common/commonConstants"
import { BreadCrumb } from "../components/commons/breadcrumb"
import { chakraTheme } from "../theme/chakraTheme"
import { Colors } from "../theme/colors"
import { Heading3PT } from "../components/commons/Heading3PT"
import { SubPage, SubPageContent } from "../components/SubPage"

export const CompliancePage = (): JSX.Element => {
  return (
    <SubPage>
      <StaticPageHeader imageOpacity={0.5} title="Understanding Compliance" bg={bg} />
      <BreadCrumb
        options={{
          ...breadCrumbFirstLevel,
          ...breadCrumbReportsAndResourcesSecondLevel,
          thirdLevel: { title: "Understanding Compliance" }
        }}
      />
      <SubPageContent innerBoxProps={{ w: ["100%", "100%", "100%", pageContentWidth, "70%", "70%"] }}>
        <Heading3PT
          text="Understanding N.C.C. Compliance and Service Penetrations"
          props={{
            mt: ["20px", "20px", "28px"],
            textAlign: "center"
          }}
        />
        <Box mt={["18px", "18px", "40px"]} w="100%">
          <Heading variant={["RHeading4", "RHeading4", "Heading3PT"]}>Compliance with the N.C.C.</Heading>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            A Building Solution will comply with the National Construction Code (N.C.C.) if it satisfies the Performance Requirements.
          </Text>
        </Box>
        <Box mt="45px" w="100%">
          <Heading variant={["RHeading4", "RHeading4", "Heading3PT"]}>Meeting the Performance Requirements</Heading>
          <Text mt="17px" variant={["RBody", "RBody", "Body1Regular"]}>
            Compliance with the Performance Requirements can only be achieved by:
          </Text>
          <Text px="18px" mt="13px" variant={["RBody", "RBody", "Body1Regular"]} textAlign="left">
            <ol style={{ paddingLeft: "4px", width: "100%" }}>
              <li>
                <Text variant={["RBody", "RBody", "Body1Regular"]}>complying with the Deemed-to-Satisfy Provisions; or</Text>
              </li>
              <li>
                <Text variant={["RBody", "RBody", "Body1Regular"]}>formulating an Alternative Solution which –</Text>
                <Box p="0" px="0" pt="1px">
                  <ol type="a" style={{ paddingLeft: "22px" }}>
                    <li>
                      <Text variant={["RBody", "RBody", "Body1Regular"]}>complies with the Performance Requirements; or</Text>
                    </li>
                    <li>
                      <Text variant={["RBody", "RBody", "Body1Regular"]}>
                        is shown to be at least equivalent to the Deemed-to-Satisfy Provisions; or
                      </Text>
                    </li>
                  </ol>
                </Box>
              </li>
              <li>combination of (a) and (b)</li>
            </ol>
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            The Performance Requirements and Deemed-to-Satisfy Provisions are found in the N.C.C. volumes and/or referenced Australian Standards (AS).
          </Text>
        </Box>
        <Box mt="45px" w="100%">
          <Heading variant={["RHeading4", "RHeading4", "Heading3PT"]}>Alternative Solutions</Heading>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            Alternative Solutions undergo a lengthy approval process through the State or Territory’s Regulatory Authorities who will ultimately
            assess the application and decide if it meets the Performance Requirements. If granted, they are only valid for the specific site and
            specific situations for which the application was made. They are not, therefore, a feasible solution for mass produced proprietary
            products. Mass produced products have to pass the Deemed-to-Satisfy tests (D-t-S Tests).
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="16px" textAlign="left">
            Products failing the D-t-S test will <span style={{ fontWeight: "bold", color: Colors.Secondary.c2 }}>NOT BE APPROVED</span> through
            Alternative Solutions.
          </Text>
        </Box>
        <Box mt="45px" w="100%">
          <Heading variant={["RHeading4", "RHeading4", "Heading3PT"]}>Deemed To Satisfy Provisions</Heading>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="18px" textAlign="left">
            The <span style={{ fontWeight: "bold", color: Colors.Secondary.c2 }}>Deemed-to-Satisfy Performance Requirement</span> for service
            penetrations is found in N.C.C. Vol.1: Section C3.15. <br />
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} pt="16px">
            C3.15 requires that an identical prototype of the proposed service penetration system (i.e. Building Element, fire collar, method of
            installation, service, and service support) has been tested to ensure that the “Fire Resistance Level” (FRL) of the element with the
            penetration protection system (e.g. fire collars and any service support) is not compromised.
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            The test must be conducted and reported according to the procedure specified in AS1530.4, and the results set out in a “Test Report” or a
            “Regulatory Information Report (RIR)“. The full Test Report is preferred as it contains all of the information relevant to the test.
            Certificates may not be used for compliance purposes; they are only to be used for commercial purposes, i.e. sales, and their exact
            wording is specified in the standards.
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            A Test Report, RIR or an Assessment (see below) are the documents required as “Evidence of Suitability” for compliance in N.C.C. Vol.1:
            Part A5 – Documentation of Design and Construction; clause A5.0 (2)(a).
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            The other clauses in section A5{" "}
            <span style={{ fontWeight: "700", color: Colors.Secondary.c2 }}>cannot be used to qualify a “Deemed-to-Satisfy solution” because</span>{" "}
            the ONLY evidence that is acceptable for a Deemed-to-Satisfy solution for penetrations is either a report covering the test specified in
            C3.15, or a formal opinion on a minor variation to the tested system which is permitted by the standards AS1530.3 and AS4072.1.
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            The other clauses in A5 are for Alternative Solutions, which as discussed above are applicable only for a specific site after a lengthy
            approval process by a relevant Regulatory Authority.
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            There is one other Deemed-to-Satisfy solution for some specific penetration systems and situations: this is installation as per{" "}
            <span style={{ fontWeight: "bold", color: Colors.Secondary.c2 }}>Specification C3.15</span>. If followed{" "}
            <span style={{ fontWeight: "bold", color: Colors.Secondary.c2 }}>exactly</span> it does not require testing, however it does contain
            conflicts regarding spacing between penetrations.
          </Text>
        </Box>
        <Box mt="45px" w="100%">
          <Heading variant={["RHeading4", "RHeading4", "Heading3PT"]}>Assessments</Heading>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            A formal opinion or “Assessment”, as they are known, is a document that has to be prepared by a registered NATA testing laboratory (see
            4.2 of AS4072.1). The scope of “Assessments” are set out in AS4074.1 Section 4: VARIATIONS PERMITTED SUBJECT TO A FORMAL OPINION.
          </Text>
          <Text variant={["RBody", "RBody", "Body1Regular"]} mt="15px" textAlign="left">
            Section 4 of AS4072.1 defines a set of rules that must be followed in order to qualify an assessment as a Deemed-to-Satisfy Building
            solution and it needs to be read in its entirety. “Assessments” should not be confused with “Alternative Solutions”, which are solely
            concerned with minor variations to tested service penetration systems. It is not practical to reproduce the rules here in full, but please
            take particular note of the following.
          </Text>
          <Box p="8px" pb="16px" {...chakraTheme.components.Text.compliancePara} textAlign="left">
            <ul style={{ paddingLeft: "10px" }}>
              <li>
                <Text variant={["RBody", "RBody", "Body1Regular"]}>
                  GENERAL 4.1 (a): Assessments shall be approved by the Regulatory Authority or other Authority having jurisdiction.
                </Text>
              </li>
              <li>
                <Text variant={["RBody", "RBody", "Body1Regular"]} pt="0">
                  PLASTIC PIPES Table 4.2 states that floorwastes and showers must be protected by collars that pass the FLOOR WASTE test. All other
                  situations only need a collar that passes the STACK test. 4.6.4: Subject to the provisions of 4.6.3 Pipes made from “Materials other
                  than PVC” and of different wall thicknesses can be assessed, but only between the sizes of 40mm and 120mm, and even then, the
                  largest and smallest pipes in this range must be tested with the penetration sealing device being assessed. 4.6.5: “Fittings within
                  the collar” are not to be used unless “supplementary testing” is also done.
                </Text>
              </li>
            </ul>
          </Box>
          <Text variant={["RBodyBold", "RBodyBold", "Body1Bold"]} fontSize={"15px"} textAlign="left" pt="0" lineHeight={"19px"}>
            <span style={{ fontWeight: "bold", color: Colors.Primary.accent }}>
              Generally, documents and opinions that contain the words “likely performance” should only be used as part of an Alternative Solution and
              not as a Deemed-to-Satisfy solution.
            </span>
          </Text>
        </Box>
      </SubPageContent>
    </SubPage>
  )
}
